/**
 * WorkflowCanvas - Main drag-and-drop canvas for workflow composition
 * 
 * Features:
 * - Drag agents from palette onto canvas
 * - Position workflow steps visually
 * - Connect steps with SVG lines
 * - Real-time workflow validation
 */

import React, { useCallback } from 'react';
import { useDroppable } from '@dnd-kit/core';

interface WorkflowStep {
  id: string;
  agentId: string;
  agentName: string;
  position: { x: number; y: number };
  configuration: Record<string, any>;
  connections: string[];  // IDs of connected steps
}

interface WorkflowDefinition {
  id?: string;
  name: string;
  description: string;
  steps: WorkflowStep[];
  fieldMappings: any[];
  businessRules: any[];
}

interface WorkflowCanvasProps {
  workflow: WorkflowDefinition;
  onChange: (workflow: WorkflowDefinition) => void;
  onStepSelect: (stepId: string) => void;
}

export const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({
  workflow,
  onChange,
  onStepSelect
}) => {
  const { setNodeRef } = useDroppable({
    id: 'workflow-canvas'
  });

  // Handle step click for selection
  const handleStepClick = useCallback((stepId: string) => {
    onStepSelect(stepId);
  }, [onStepSelect]);

  return (
    <div
      ref={setNodeRef}
      id="workflow-canvas"
      className="relative w-full min-h-[600px] rounded-lg border-2 border-dashed border-gray-300"
      style={{
        background: 'linear-gradient(90deg, #f0f0f0 1px, transparent 1px), linear-gradient(#f0f0f0 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }}
    >
      {/* Render connections */}
      <svg
        className="absolute top-0 left-0 w-full h-full pointer-events-none z-[1]"
      >
        {workflow.steps.flatMap((step: WorkflowStep) =>
          step.connections.map((targetId: string) => {
            const target = workflow.steps.find((s: WorkflowStep) => s.id === targetId);
            if (!target) return null;

            return (
              <line
                key={`${step.id}-${targetId}`}
                x1={step.position.x + 60}
                y1={step.position.y + 40}
                x2={target.position.x + 60}
                y2={target.position.y + 40}
                stroke="#3b82f6"
                strokeWidth="2"
                markerEnd="url(#arrowhead)"
              />
            );
          })
        )}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#3b82f6"
            />
          </marker>
        </defs>
      </svg>

      {/* Render workflow steps */}
      {workflow.steps.map((step: WorkflowStep) => (
        <div
          key={step.id}
          className="absolute z-[2] p-4 bg-white border-2 border-blue-500 rounded-lg shadow-md min-w-[150px] cursor-pointer"
          style={{
            left: step.position.x,
            top: step.position.y
          }}
          onClick={() => handleStepClick(step.id)}
        >
          <div className="font-bold text-sm">
            {step.agentName}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {step.agentId}
          </div>
        </div>
      ))}

      {/* Empty state */}
      {workflow.steps.length === 0 && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-gray-400 text-base">
          <p>Drag agents from the palette to start building your workflow</p>
        </div>
      )}
    </div>
  );
};
